import { DateTime } from "luxon";
import { useCallback, useEffect } from "react";

import { CALL_STATUS } from "@/constants/call";
import { SIMULATED_CALL_ID_PREFIX, SIMULATED_CALL_PROVIDER } from "@/constants/call.dev";
import { ENV } from "@/constants/env";
import { Call, CallPayload, CallStatus } from "@/types/call.types";

type UseSimulateDevCallDependencies = {
    callStatus: CallStatus;
    currentCallData: Call | undefined;
    userData?: { id?: string; name?: string };
    setCalledPhoneNumber: (phoneNumber: string) => void;
    setCurrentCallData: (call: Call | undefined) => void;
    setCallStartTime: (time: string | null) => void;
    setCallStatus: (status: CallStatus) => void;
};

/**
 * Development-only hook for simulating calls without Twilio.
 * This hook provides functions to test the call UI without making actual calls.
 * Only available when ENV.DEV is true.
 */
export function useSimulateDevCall({
    callStatus,
    currentCallData,
    userData,
    setCalledPhoneNumber,
    setCurrentCallData,
    setCallStartTime,
    setCallStatus,
}: UseSimulateDevCallDependencies) {
    const simulateCallStart = useCallback(
        (phoneNumber: string, callData?: Omit<Partial<CallPayload>, "To" | "From">) => {
            if (!ENV.DEV) return;

            if (callStatus !== CALL_STATUS.DISCONNECTED) return;

            const now = DateTime.now().toISO();
            const simulatedCallId = `${SIMULATED_CALL_ID_PREFIX}${DateTime.now().toMillis()}`;

            const simulatedCall: Call = {
                id: simulatedCallId,
                userId: userData?.id || simulatedCallId,
                orgId: callData?.orgId || "sim-org",
                provider: SIMULATED_CALL_PROVIDER,
                callSid: null,
                callStartTime: now,
                callEndTime: undefined,
                callerNumber: null,
                callerName: userData?.name || "Simulated User",
                calledNumber: phoneNumber,
                calledName: "Simulated Lead",
                calledEmail: null,
                leadId: callData?.recordId || "sim-lead",
                moduleType: callData?.moduleType,
                recordId: callData?.recordId,
                callPurpose: "SIMULATION",
                callDuration: null,
                callAttempts: 1,
                callResult: null,
                callRecordingUrl: null,
                callNotes: null,
                callTranscript: null,
                statusLogs: [
                    {
                        status: CALL_STATUS.CONNECTED,
                        timestamp: now,
                    },
                ],
                status: CALL_STATUS.CONNECTED,
                createdAt: now,
                updatedAt: now,
            };

            setCalledPhoneNumber(phoneNumber);
            setCurrentCallData(simulatedCall);
            setCallStartTime(simulatedCall.callStartTime || null);
            setCallStatus(CALL_STATUS.CONNECTED);
        },
        [
            callStatus,
            userData?.id,
            userData?.name,
            setCalledPhoneNumber,
            setCurrentCallData,
            setCallStartTime,
            setCallStatus,
        ]
    );

    const simulateCallEnd = useCallback(() => {
        if (!ENV.DEV) return;

        if (callStatus === CALL_STATUS.DISCONNECTED) return;
        if (currentCallData?.provider !== SIMULATED_CALL_PROVIDER) return;

        setCallStatus(CALL_STATUS.DISCONNECTED);
        setCallStartTime(null);
    }, [callStatus, currentCallData?.provider, setCallStatus, setCallStartTime]);

    // Cleanup simulated call data when call ends
    useEffect(() => {
        if (!ENV.DEV) return;
        if (callStatus !== CALL_STATUS.DISCONNECTED) return;
        if (currentCallData?.provider !== SIMULATED_CALL_PROVIDER) return;

        setCurrentCallData(undefined);
    }, [callStatus, currentCallData?.provider, setCurrentCallData]);

    return {
        simulateCallStart,
        simulateCallEnd,
    };
}
